/**
 * 시공사례 입력 스키마. 화면의 zod 는 빨간 글씨용이고, 저장되는 값의 기준은 여기다.
 *
 * 이미지는 URL 만 받는다 — 업로드(Storage)가 붙기 전까지는 손으로 넣은 주소가
 * 그대로 고객 화면에 실린다. 그래서 `https://` 가 아닌 것은 아예 안 받는다
 * (`http://` 는 혼합 콘텐츠로 막히고, `javascript:`·`data:` 는 위험하다).
 */

import { z } from "zod";

import { AREA_BANDS, RESIDENCES } from "../customer/types";
import { excerptLineIssues, excerptLinesOf } from "./normalize";
import { isProjectPhotoPath } from "./photo-path";
import { PORTFOLIO_IMAGE_STAGES, PORTFOLIO_PUBLIC_FIELDS } from "./types";

/** 한 사례에 넣을 수 있는 이미지 수. 상담 화면에서 넘겨 볼 만한 상한. */
export const PORTFOLIO_MAX_IMAGES = 30;

// 금액 상한 — 1조 원(오타로 자리가 밀린 값을 막는다).
export const PORTFOLIO_AMOUNT_MAX = 1_000_000_000_000;

export const PORTFOLIO_URL_MESSAGE =
  "https:// 로 시작하는 주소만 넣을 수 있습니다";

/** 스킴까지 보고, 호스트가 있는지도 본다. 정규식만으로는 `https://` 하나도 통과한다. */
const isHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname.length > 0;
  } catch {
    return false;
  }
};

const httpsUrl = z
  .string()
  .trim()
  .max(1000, "주소가 너무 깁니다")
  .refine(isHttpsUrl, PORTFOLIO_URL_MESSAGE);

/** 빈 문자열은 "고르지 않음". 폼 셀렉트가 그대로 넘긴다. */
const nullableEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .enum(values)
    .nullable()
    .optional()
    .transform((v) => v ?? null);

// 현장 사진(photo_id)은 url 을 서버가 다시 쓰므로 https 검사를 건너뛴다 — 그 밖의 장은 https 만.
export const portfolioImageSchema = z
  .object({
    url: z.string().trim().max(1000, "주소가 너무 깁니다"),
    caption: z.string().trim().max(200, "캡션은 200자 이내로 적어 주세요").default(""),
    photo_id: z.string().trim().min(1).max(100).optional(),
    // 사진 태그 — 공간 코드가 조직 목록에 있는지는 액션이 조직 코드로 거른다.
    stage: z.enum(PORTFOLIO_IMAGE_STAGES).optional(),
    space: z.string().trim().max(30).optional(),
  })
  .superRefine((image, ctx) => {
    if (image.photo_id === undefined && !isHttpsUrl(image.url)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["url"], message: PORTFOLIO_URL_MESSAGE });
    }
  });

// 공정 챕터 하나의 발췌 줄 — 빈 줄은 버리고 줄 수·줄 길이는 초안 생성과 같은 상한.
const timelineExcerptLinesSchema = z
  .array(z.string().max(1000, "발췌 줄이 너무 깁니다"))
  .max(50, "발췌 줄이 너무 많습니다")
  .transform((lines, ctx) => {
    const kept = excerptLinesOf(lines);
    for (const message of excerptLineIssues(kept)) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    return kept;
  });

/**
 * 등록·편집이 같은 스키마를 쓴다. 폼이 늘 모든 칸을 보내기 때문에 부분 패치를
 * 따로 두지 않는다 — 두면 "안 보낸 칸" 의 뜻이 폼과 액션에서 갈린다.
 */
export const portfolioCreateSchema = z.object({
  title: z.string().trim().min(1, "제목을 입력하세요").max(100, "100자 이내로 적어 주세요"),
  summary: z.string().trim().max(500, "500자 이내로 적어 주세요").default(""),
  body: z.string().trim().max(2000, "본문은 2000자 이내로 적어 주세요").default(""),
  area_band: nullableEnum(AREA_BANDS),
  residence: nullableEnum(RESIDENCES),
  /** 비우면 저장소가 첫 장을 대표로 삼는다. */
  cover_url: z
    .union([httpsUrl, z.literal(""), z.string().trim().refine(isProjectPhotoPath, PORTFOLIO_URL_MESSAGE)])
    .optional()
    .transform((v) => v ?? ""),
  images: z
    .array(portfolioImageSchema)
    .max(PORTFOLIO_MAX_IMAGES, `이미지는 ${PORTFOLIO_MAX_IMAGES}장까지 넣을 수 있습니다`)
    .default([]),
  tags: z
    .array(z.string().trim().min(1).max(30, "태그는 30자 이내로 적어 주세요"))
    .max(20, "태그는 20개까지 넣을 수 있습니다")
    .default([]),
  published: z.boolean().default(false),
  // 공개 데이터 칸은 안 보내면(undefined) 그대로 둔다 — 빈 값은 「지움」 이다.
  designer_id: z
    .string()
    .trim()
    .max(100)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v || null)),
  amount_won: z
    .number({ invalid_type_error: "금액은 숫자로 적어 주세요" })
    .int("금액은 원 단위 정수로 적어 주세요")
    .min(0, "금액은 0원 이상이어야 합니다")
    .max(PORTFOLIO_AMOUNT_MAX, "금액이 너무 큽니다")
    .nullable()
    .optional(),
  public_fields: z
    .array(z.enum(PORTFOLIO_PUBLIC_FIELDS, { errorMap: () => ({ message: "알 수 없는 공개 항목입니다" }) }))
    .max(PORTFOLIO_PUBLIC_FIELDS.length)
    .optional(),
  // 편집만 보낸다 — 챕터별 발췌 줄. 챕터 수 대조와 나머지 칸 유지는 저장소의 병합 함수가 한다.
  timeline_excerpts: z.array(timelineExcerptLinesSchema).max(100, "공정 챕터가 너무 많습니다").optional(),
});

/** 드래그 정렬이 보내는 모양. 저장소가 중복·모르는 id 를 다시 본다. */
export const portfolioReorderSchema = z.object({
  ids: z.array(z.string().trim().min(1)).min(1, "정렬할 사례가 없습니다"),
  // 화면이 읽을 때 본 조직 전체 순서 — 지금 순서와 다르면 저장소가 거절한다(0061).
  expected: z.array(z.string().trim().min(1)),
});
